import { Avatar, Stack, Typography } from '@mui/material'
import {
    DataGridPro,
    useGridApiRef,
    type GridColDef,
} from '@mui/x-data-grid-pro'
import { GetCandidatesForMappingQuery } from '__generated__/graphql'
import { GetCandidatesForMapping } from 'client/graphql/search.gql'
import React, { useCallback, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { CenteredBox } from 'shared/components/CenteredBox'
import { LoadingPanel } from 'shared/components/LoadingPanel'
import { usePagination } from 'shared/hooks/usePagination'
import {
    getInitialsFromPreferredName,
    getPreferredName,
    getPxAsRem,
} from 'shared/utils'

export interface CandidateColumnsProps {
    rosterId: string
}

const pageLimit = 15
const defaultColumnWidth = 150

const getNoRowsOverlay = () => (
    <CenteredBox>
        <Typography>No Results</Typography>
    </CenteredBox>
)

const CandidateMappingDataGrid: React.FC<CandidateColumnsProps> = ({
    rosterId,
}) => {
    const gridApi = useGridApiRef()
    const [searchParams, setSearchParams] = useSearchParams()

    const urlPage = parseInt(searchParams.get('page') || '1', 10)

    const { data, loadMoreData, isFetching } = usePagination<
        GetCandidatesForMappingQuery['getCandidatesForMapping']['data'][number]
    >({
        query: GetCandidatesForMapping,
        variables: {
            rosterId,
            limit: pageLimit,
        },
        skip: !rosterId,
        queryPath: 'getCandidatesForMapping',
        limit: pageLimit,
    })

    useEffect(() => {
        if (urlPage > 1 && data.length < urlPage * pageLimit) {
            const pagesToLoad = urlPage - Math.ceil(data.length / pageLimit)
            for (let i = 0; i < pagesToLoad; i++) {
                loadMoreData()
            }
        }
    }, [urlPage, data.length, loadMoreData])

    const handleRowsScrollEnd = useCallback(() => {
        loadMoreData()
        const currentPage = Math.ceil((data.length + pageLimit) / pageLimit)
        setSearchParams(
            (prev) => {
                prev.set('page', currentPage.toString())
                return prev
            },
            { replace: false }
        )
    }, [loadMoreData, data.length, setSearchParams])

    const columnDefinitions: GridColDef<
        GetCandidatesForMappingQuery['getCandidatesForMapping']['data'][number]
    >[] = [
        {
            field: 'avatar',
            headerName: '',
            hideSortIcons: true, // Hide sort icons for this column
            width: 60,
            sortable: false, // Disable sorting for this column
            headerClassName: 'no-separator',
            renderCell: ({ row }) => {
                const profileImage = row.internalProfileImageKey
                return (
                    <Stack
                        justifyContent={'center'}
                        alignContent={'center'}
                        height={'100%'}
                    >
                        <Avatar key={row.email} src={profileImage || ''}>
                            {getInitialsFromPreferredName(row.firstName || '')}
                        </Avatar>
                    </Stack>
                )
            },
        },
        {
            field: 'firstName',
            headerName: 'First Name',
            sortable: false,
            flex: 1,
            minWidth: defaultColumnWidth,
            renderCell: ({ row }) => {
                return (
                    <Stack height="100%" justifyContent={'center'}>
                        <Typography noWrap variant="body2">
                            {getPreferredName({
                                firstName: row.firstName,
                                nickname: row.nickname,
                            })}
                        </Typography>
                    </Stack>
                )
            },
        },
        {
            field: 'lastName',
            headerName: 'Last Name',
            minWidth: 100,
            flex: 1,

            renderCell: ({ row }) => {
                return (
                    <Stack justifyContent="center" height="100%">
                        <Typography noWrap variant="body2">
                            {row.lastName}
                        </Typography>
                    </Stack>
                )
            },
        },
        {
            field: 'company',
            headerName: 'Company',
            flex: 1,

            sortable: false,
            renderCell: ({ row }) => {
                // Assuming the candidate has a company associated with them
                const company = row.currentCompany ?? 'N/A'
                // You can customize this further based on your needs
                return (
                    <Stack justifyContent="center" height="100%">
                        <Typography noWrap variant="body2">
                            {company}
                        </Typography>{' '}
                    </Stack>
                )
            },
            minWidth: defaultColumnWidth,
        },
        {
            field: 'jobTitle',
            headerName: 'Title',
            flex: 1,

            sortable: false,
            minWidth: defaultColumnWidth,
            renderCell: ({ row }) => {
                const jobTitle = row.currentTitle ?? 'N/A'
                return (
                    <Stack justifyContent="center" height="100%">
                        <Typography noWrap variant="body2">
                            {jobTitle}
                        </Typography>
                    </Stack>
                )
            },
        },
        {
            field: 'industry',
            headerName: 'Industry',
            flex: 1,

            sortable: false,
            renderCell: ({ row }) => {
                const industries = row.currentIndustry ?? 'N/A'

                return (
                    <Stack justifyContent="center" height="100%">
                        <Typography noWrap variant="body2">
                            {industries}
                        </Typography>
                    </Stack>
                )
            },
            minWidth: defaultColumnWidth,
        },
        {
            field: 'sector',
            headerName: 'Sector',
            flex: 1,
            renderCell: ({ row }) => {
                const sectors = row.currentSector ?? 'N/A'
                return (
                    <Stack justifyContent="center" height="100%">
                        <Typography noWrap variant="body2">
                            {sectors}
                        </Typography>
                    </Stack>
                )
            },
            sortable: false,
            minWidth: defaultColumnWidth,
        },
        {
            field: 'location',
            headerName: 'Location',
            flex: 1,

            minWidth: defaultColumnWidth,
            renderCell: ({ row }) => {
                // Assuming the candidate has a location associated with them
                const location = row.currentLocation ?? 'N/A'
                // You can customize this further based on your needs
                return (
                    <Stack height="100%" justifyContent={'center'}>
                        <Typography noWrap variant="body2">
                            {location}
                        </Typography>
                    </Stack>
                )
            },
        },
    ]

    return (
        <DataGridPro
            sx={{
                height: '100%',
                overflow: 'auto',
                minHeight: data.length > 0 ? undefined : getPxAsRem(400),
                '& .no-separator .MuiDataGrid-columnSeparator': {
                    display: 'none', // Hides the separator for specific columns
                },
            }}
            apiRef={gridApi}
            rows={data}
            columns={columnDefinitions}
            loading={isFetching}
            getRowId={(row) => `${row.id}`}
            hideFooter
            disableColumnFilter
            disableColumnSelector
            disableColumnSorting
            disableColumnMenu
            disableRowSelectionOnClick
            onRowsScrollEnd={handleRowsScrollEnd}
            scrollEndThreshold={100}
            slots={{
                noRowsOverlay: getNoRowsOverlay,
                loadingOverlay: () => <LoadingPanel />,
            }}
        />
    )
}

export default CandidateMappingDataGrid
